import {
  Box,
  BoxProps,
  LinearProgress,
  SxProps,
  Typography,
} from '@mui/material';
import { IconBoldPauseCircle } from 'components/icons/components/bold/IconBoldPauseCircle';
import { IconBoldPlayCircle } from 'components/icons/components/bold/IconBoldPlayCircle';
import { IconBoldVolumeHigh } from 'components/icons/components/bold/IconBoldVolumeHigh';
import { IconBoldVolumeSlash } from 'components/icons/components/bold/IconBoldVolumeSlash';
import { IconLinearMaximize4 } from 'components/icons/components/linear/IconLinearMaximize4';
import { IconLinearMinimize } from 'components/icons/components/linear/IconLinearMinimize';
import { IconOutlineBackward5Seconds } from 'components/icons/components/outline/IconOutlineBackward5Seconds';
import { IconOutlineForward5Seconds } from 'components/icons/components/outline/IconOutlineForward5Seconds';
import { useEffect, useRef, useState } from 'react';
import { theme } from 'styles/theme';
import { PlaybackSpeedSelect } from './PlaybackSpeedSelect';
import { StyledIconButton } from './styles';
import { RenderVideoTimeMarker } from './types';

export const VIDEO_CONTROLS_SM_SIZE_WIDTH_THRESHOLD = 240;
export const VIDEO_CONTROLS_XS_SIZE_WIDTH_THRESHOLD = 160;
export const VIDEO_CONTROLS_XXS_SIZE_WIDTH_THRESHOLD = 120;

export type VideoControlsProps = {
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  duration: number;
  currentTime: number;
  seekbarValue: number;
  onSeekbarClicked: (e: React.MouseEvent<HTMLDivElement>) => void;
  onSeekBackward: () => void;
  onSeekForward: () => void;
  playVideo: () => void;
  playbackSpeed: number;
  setPlaybackSpeed: React.Dispatch<React.SetStateAction<number>>;
  isFullScreen: boolean;
  onFullScreen: () => void;
  renderVideoTimeMarkers: RenderVideoTimeMarker[];
  boxProps?: BoxProps;
  // default is show all controls
  // if showControls is provided, only show the controls that are true
  showControls?: {
    playPause?: boolean;
    seekBackward?: boolean;
    seekForward?: boolean;
    currentTime?: boolean;
    volume?: boolean;
    playbackSpeed?: boolean;
    fullScreen?: boolean;
  };
  controlsBarMode?: 'default' | 'bar';
  iconSize?: number;
  isMuted: boolean;
  onToggleMute: () => void;
};

export const VideoControls = (props: VideoControlsProps) => {
  const {
    videoRef,
    duration,
    currentTime,
    seekbarValue,
    onSeekbarClicked,
    onSeekBackward,
    onSeekForward,
    playVideo,
    playbackSpeed,
    setPlaybackSpeed,
    isFullScreen,
    onFullScreen,
    renderVideoTimeMarkers,
    boxProps,
    showControls: _showControls,
    controlsBarMode = 'default',
    iconSize = 24,
    isMuted,
    onToggleMute,
  } = props;

  // State for layout size
  const [layout, setLayout] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  // Ref for the controls container
  const controlsRef = useRef<HTMLDivElement>(null);

  let timeFontSize = 12;

  // Custom Video Controls Responsive
  const isSmSize =
    layout.width > 0 && layout.width < VIDEO_CONTROLS_SM_SIZE_WIDTH_THRESHOLD;

  const isXsSize =
    layout.width > 0 && layout.width < VIDEO_CONTROLS_XS_SIZE_WIDTH_THRESHOLD;

  const isXxsSize =
    layout.width > 0 && layout.width < VIDEO_CONTROLS_XXS_SIZE_WIDTH_THRESHOLD;

  // if small layout, auto reduce icon size by 4px
  const iconSizeAutoFit = isSmSize ? iconSize - 4 : iconSize;

  // Merge showControls with responsive override for back/forward
  const showControls = {
    playPause: true,
    seekBackward: true,
    seekForward: true,
    currentTime: true,
    volume: true,
    playbackSpeed: true,
    fullScreen: true,
    ..._showControls,
  };
  if (isSmSize) {
    showControls.seekBackward = false;
    showControls.seekForward = false;
    timeFontSize = 10;
  }
  if (isXsSize) {
    showControls.playbackSpeed = false;
    timeFontSize = 8;
  }
  if (isXxsSize) {
    showControls.fullScreen = false;
  }

  // Setup ResizeObserver to track layout changes
  useEffect(() => {
    if (!controlsRef.current) return;
    const node = controlsRef.current;
    const observer = new window.ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect) {
          setLayout({
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          });
        }
      }
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, []);

  const [hidden, setHidden] = useState(false);
  const fadeOutTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const onMouseMove = () => {
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
      }

      fadeOutTimeoutRef.current = setTimeout(() => {
        setHidden(true);
      }, 3000);

      setHidden(false);
    };

    const onMouseLeave = () => {
      fadeOutTimeoutRef.current = setTimeout(() => {
        setHidden(true);
      }, 3000);
    };

    videoRef.current?.addEventListener('mousemove', onMouseMove);
    videoRef.current?.addEventListener('mouseleave', onMouseLeave);

    return () => {
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
      }

      videoRef.current?.removeEventListener('mousemove', onMouseMove);
      videoRef.current?.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []); // eslint-disable-line

  const barContainerStyle: SxProps =
    controlsBarMode === 'default'
      ? {
          bottom: theme.spacing(2),
          width: 'calc(100% - 32px)',
          left: '16px',
        }
      : {
          bottom: theme.spacing(2),
          left: 4,
          width: 'calc(100% - 8px)',
        };

  const barInnerContainerStyle: SxProps =
    controlsBarMode === 'default'
      ? {
          height: theme.spacing(10),
          display: 'flex',
          borderRadius: 16,
          backdropFilter: `blur(2px)`,
          alignItems: 'center',
        }
      : {
          borderRadius: 100,
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: theme.spacing(2),
          background: 'rgba(35, 6, 3, 0.2)',
          backdropFilter: 'blur(40px)',
        };

  return (
    <Box
      ref={controlsRef} // Attach ref for ResizeObserver
      sx={{
        transition: 'opacity 0.3s',
        opacity: hidden ? 0 : 1,
        position: 'absolute',
        display: 'flex',
        flexDirection: 'column',
        ...barContainerStyle,
        ...boxProps?.sx,
      }}
      {...boxProps}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* video time markers above the controls bar */}
      <Box
        sx={{
          position: 'absolute',
          top: controlsBarMode === 'bar' ? 0 : -8,
          left: 8,
          width: 'calc(100% - 16px - 8px)',
        }}
      >
        {!!renderVideoTimeMarkers.length && !!duration && videoRef.current && (
          <Box
            sx={{
              height: '1px',
              position: 'absolute',
              top: theme.spacing(-1),
              width: 'inherit',
            }}
          >
            {renderVideoTimeMarkers.map((marker) => {
              return (
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${(marker.timeInSeconds / duration) * 100}%`,
                    bottom: 0,
                    transform: 'translateX(-50%)',
                  }}
                >
                  {marker.renderUI()}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          ...barInnerContainerStyle,
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          height: '100%',
          overflow: controlsBarMode === 'bar' ? 'hidden' : 'visible',
        }}
      >
        {/* Seekbar */}
        <LinearProgress
          variant="determinate"
          value={seekbarValue || 0}
          onClick={onSeekbarClicked}
          sx={{
            cursor: 'pointer',
            borderRadius: 1,
            height: theme.spacing(1),
            position: 'absolute',
            top: controlsBarMode === 'bar' ? 0 : -8,
            left: 0,
            width: 'inherit',
            bgcolor: theme.colors?.utility[700],
            '& .MuiLinearProgress-bar': {
              bgcolor: theme.colors?.primary.white,
            },
          }}
        />
        <Box display="flex" gap={1} alignItems="center" justifyContent="center">
          {/* Play/Pause */}
          {showControls?.playPause && (
            <StyledIconButton
              disableRipple
              onClick={() =>
                videoRef.current?.paused
                  ? playVideo()
                  : videoRef.current?.pause()
              }
            >
              {videoRef.current?.paused ? (
                <IconBoldPlayCircle
                  color={theme.colors?.primary.white}
                  size={iconSizeAutoFit}
                />
              ) : (
                <IconBoldPauseCircle
                  color={theme.colors?.primary.white}
                  size={iconSizeAutoFit}
                />
              )}
            </StyledIconButton>
          )}
          {/* Seek backward 5 seconds */}
          {showControls?.seekBackward && (
            <StyledIconButton disableRipple onClick={onSeekBackward}>
              <IconOutlineBackward5Seconds
                color={theme.colors?.primary.white}
                size={iconSizeAutoFit}
              />
            </StyledIconButton>
          )}
          {/* Seek forward 5 seconds */}
          {showControls?.seekForward && (
            <StyledIconButton disableRipple onClick={onSeekForward}>
              <IconOutlineForward5Seconds
                color={theme.colors?.primary.white}
                size={iconSizeAutoFit}
              />
            </StyledIconButton>
          )}
        </Box>
        {/* Current time / duration */}
        {showControls?.currentTime && (
          <Box
            display="flex"
            flex={1}
            justifyContent="center"
            color={theme.colors?.primary.white}
          >
            <Typography
              variant="body-sm"
              fontSize={timeFontSize}
              fontWeight={600}
            >
              {new Date(currentTime * 1000).toISOString().slice(14, 19)} /{' '}
              {new Date(duration * 1000).toISOString().slice(14, 19)}
            </Typography>
          </Box>
        )}
        <Box
          display="flex"
          gap={isSmSize ? 0.5 : 1}
          alignItems="center"
          justifyContent="flex-end"
        >
          {/* Mute/Unmute */}
          {showControls?.volume && (
            <StyledIconButton disableRipple onClick={onToggleMute}>
              {isMuted ? (
                <IconBoldVolumeSlash
                  color={theme.colors?.primary.white}
                  size={iconSizeAutoFit}
                />
              ) : (
                <IconBoldVolumeHigh
                  color={theme.colors?.primary.white}
                  size={iconSizeAutoFit}
                />
              )}
            </StyledIconButton>
          )}
          {/* Playback speed select: 0.5x, 1x, 1.5x, 2x */}
          {showControls?.playbackSpeed && (
            <PlaybackSpeedSelect
              onPlaybackSpeedChanged={(speed) => {
                setPlaybackSpeed(speed);
                if (!videoRef || !videoRef.current) return;
                videoRef.current.playbackRate = speed as number;
              }}
              playbackSpeed={playbackSpeed}
              componentsProps={{
                text: {
                  fontSize: timeFontSize,
                },
              }}
            />
          )}
          {/* Full screen */}
          {showControls?.fullScreen && (
            <StyledIconButton disableRipple onClick={onFullScreen}>
              {isFullScreen ? (
                <IconLinearMinimize
                  color={theme.colors?.primary.white}
                  size={iconSizeAutoFit}
                />
              ) : (
                <IconLinearMaximize4
                  size={iconSizeAutoFit}
                  color={theme.colors?.primary.white}
                />
              )}
            </StyledIconButton>
          )}
        </Box>
      </Box>
    </Box>
  );
};
